import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';

export interface QueueItem {
  id: string;
  title: string;
  text: string;
  articleId?: string;
  timestamp: number;
}

export interface TTSSettings {
  rate: number; // 0.5-2.0, default 1.0
  pitch: number; // 0.5-2.0, default 1.0
  volume: number; // 0-1, default 1.0
  voiceURI: string; // selected voice
}

interface TTSQueueContextType {
  queue: QueueItem[];
  currentIndex: number;
  isPlaying: boolean;
  settings: TTSSettings;
  addToQueue: (item: Omit<QueueItem, 'timestamp'>) => void;
  removeFromQueue: (id: string) => void;
  clearQueue: () => void;
  playItem: (index: number) => void;
  playNext: () => void;
  playPrevious: () => void;
  reorderQueue: (fromIndex: number, toIndex: number) => void;
  setPlaying: (playing: boolean) => void;
  getCurrentItem: () => QueueItem | null;
  updateSettings: (settings: TTSSettings) => void;
}

const TTSQueueContext = createContext<TTSQueueContextType | undefined>(undefined);

const STORAGE_KEY = 'tts_queue';
const SETTINGS_KEY = 'tts_settings';

const DEFAULT_SETTINGS: TTSSettings = {
  rate: 1.0,
  pitch: 1.0,
  volume: 1.0,
  voiceURI: '',
};

export const TTSQueueProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [queue, setQueue] = useState<QueueItem[]>(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });
  const [currentIndex, setCurrentIndex] = useState(-1);
  const [isPlaying, setIsPlaying] = useState(false);
  const [settings, setSettings] = useState<TTSSettings>(() => {
    try {
      const saved = localStorage.getItem(SETTINGS_KEY);
      return saved ? JSON.parse(saved) : DEFAULT_SETTINGS;
    } catch {
      return DEFAULT_SETTINGS;
    }
  });

  // Persist queue to localStorage
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(queue));
    } catch (error) {
      console.error('Failed to save TTS queue:', error);
    }
  }, [queue]);

  // Persist settings to localStorage
  useEffect(() => {
    try {
      localStorage.setItem(SETTINGS_KEY, JSON.stringify(settings));
    } catch (error) {
      console.error('Failed to save TTS settings:', error);
    }
  }, [settings]);

  const addToQueue = useCallback((item: Omit<QueueItem, 'timestamp'>) => {
    const newItem: QueueItem = {
      ...item,
      timestamp: Date.now(),
    };

    setQueue((prev) => {
      // Check if item already exists
      const exists = prev.some((i) => i.id === item.id);
      if (exists) {
        return prev;
      }
      return [...prev, newItem];
    });
  }, []);

  const removeFromQueue = useCallback(
    (id: string) => {
      setQueue((prev) => {
        const index = prev.findIndex((item) => item.id === id);
        if (index === -1) return prev;

        const newQueue = prev.filter((item) => item.id !== id);

        // Adjust current index if needed
        if (index === currentIndex) {
          setCurrentIndex(-1);
          setIsPlaying(false);
        } else if (index < currentIndex) {
          setCurrentIndex((prev) => prev - 1);
        }

        return newQueue;
      });
    },
    [currentIndex],
  );

  const clearQueue = useCallback(() => {
    setQueue([]);
    setCurrentIndex(-1);
    setIsPlaying(false);
  }, []);

  const playItem = useCallback(
    (index: number) => {
      if (index >= 0 && index < queue.length) {
        setCurrentIndex(index);
        setIsPlaying(true);
      }
    },
    [queue.length],
  );

  const playNext = useCallback(() => {
    if (currentIndex < queue.length - 1) {
      setCurrentIndex((prev) => prev + 1);
      setIsPlaying(true);
    } else {
      setIsPlaying(false);
    }
  }, [currentIndex, queue.length]);

  const playPrevious = useCallback(() => {
    if (currentIndex > 0) {
      setCurrentIndex((prev) => prev - 1);
      setIsPlaying(true);
    }
  }, [currentIndex]);

  const reorderQueue = useCallback(
    (fromIndex: number, toIndex: number) => {
      setQueue((prev) => {
        const newQueue = [...prev];
        const [movedItem] = newQueue.splice(fromIndex, 1);
        newQueue.splice(toIndex, 0, movedItem);

        // Adjust current index
        if (currentIndex === fromIndex) {
          setCurrentIndex(toIndex);
        } else if (fromIndex < currentIndex && toIndex >= currentIndex) {
          setCurrentIndex((prev) => prev - 1);
        } else if (fromIndex > currentIndex && toIndex <= currentIndex) {
          setCurrentIndex((prev) => prev + 1);
        }

        return newQueue;
      });
    },
    [currentIndex],
  );

  const setPlaying = useCallback((playing: boolean) => {
    setIsPlaying(playing);
  }, []);

  const getCurrentItem = useCallback(() => {
    if (currentIndex >= 0 && currentIndex < queue.length) {
      return queue[currentIndex];
    }
    return null;
  }, [currentIndex, queue]);

  const updateSettings = useCallback((newSettings: TTSSettings) => {
    setSettings(newSettings);
  }, []);

  return (
    <TTSQueueContext.Provider
      value={{
        queue,
        currentIndex,
        isPlaying,
        settings,
        addToQueue,
        removeFromQueue,
        clearQueue,
        playItem,
        playNext,
        playPrevious,
        reorderQueue,
        setPlaying,
        getCurrentItem,
        updateSettings,
      }}
    >
      {children}
    </TTSQueueContext.Provider>
  );
};

export const useTTSQueue = () => {
  const context = useContext(TTSQueueContext);
  if (!context) {
    throw new Error('useTTSQueue must be used within TTSQueueProvider');
  }
  return context;
};
